import {
	ErrorModal,
	getMomentTz,
	useContextMenuMESF,
} from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClassParams,
	RowSelectedEvent,
	RowStyle,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Moment } from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	EndFilter,
	StartFilter,
} from "@/pages/delays/shared/components/StartEndFilter";

const moment = getMomentTz();

import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { routeMillSchedule } from "@/setup/routes/mill.routes";
import { DeleteEntity } from "@/shared/components/DeleteEntity";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { Group, Panel } from "react-resizable-panels";
import { useNavigate } from "react-router-dom";
import { HeatChips } from "../../overview/components/HeatChips";
import { useCampaignDelays, useCampaigns } from "../CampaignPage";
import { useCampaignOptionsFunctions } from "../hooks/useCampaignOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { Campaign } from "../models/Campaign";
import { deleteCampaign } from "../repositories/CampaignRepository";
import { CampaignDetails } from "./CampaignDetails";

export const TableCampaign = ({
	campaignId,
}: {
	campaignId: string | undefined;
}) => {
	const navigate = useNavigate();

	const [filterValue, setFilterValue] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<Campaign | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [error, setError] = useState("");

	// Date filters - default: last 30 days
	const [filterStartDate, setFilterStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(
		moment().endOf("day"),
	);

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("mill-schedule-table-panel-sizes");

	const { data: rowsCampaign, isLoading: isLoadingCampaign } = useCampaigns({
		start: filterStartDate.toDate(),
		end: filterEndDate.toDate(),
		onError: (error) => setError(error.message),
	});

	const { data: rowsCampaignDelays } = useCampaignDelays({
		start: filterStartDate.toDate(),
		end: filterEndDate.toDate(),
		onError: (error) => setError(error.message),
	});

	const rows = useMemo(() => {
		if (!rowsCampaign) return [];

		const delaysMap = new Map(
			rowsCampaignDelays?.map((delay) => [delay.CampaignId, delay]) ?? [],
		);

		return rowsCampaign.map((campaign) => {
			const delayData = delaysMap.get(campaign.CampaignId);
			return {
				...campaign,
				DelayTimePercent: delayData?.DelayTimePercent ?? 0,
				TotalDelayTime: delayData?.TotalDelayTime ?? 0,
			} as Campaign;
		});
	}, [rowsCampaign, rowsCampaignDelays]);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useCampaignOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});

	const { columnDefs } = useTableData();

	const { defaultColDef } = useGridDefinitions({});

	const onRowSelected = (event: RowSelectedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			setSelectedRowData(event.data);
			setShowModal("update");
			navigate(`${routeMillSchedule}/${(event.data as Campaign).CampaignId}`);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "campaignContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rowsCampaign, rowsCampaignDelays],
	);

	const getRowStyle = (
		params: RowClassParams<Campaign, any>,
	): RowStyle | undefined => {
		const campaign = params.data;
		if (!campaign) return undefined;
		if (campaign.SampleStatusCode === "U")
			return { backgroundColor: "#fbe3e5ff" };

		if (campaign.SampleStatusCode === "R") {
			return { backgroundColor: "#fcf5dbff" };
		}
		if (campaign.SampleStatusCode === "P") {
			return { backgroundColor: "#e3fbeaff" };
		}
		return undefined;
	};

	const onModalHide = ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			setTimeout(() => {
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode!) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	const handleNewCampaign = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`${routeMillSchedule}/new`);
	};

	useEffect(() => {
		if (!rowsCampaign) {
			if (campaignId) {
				if (campaignId === "new") {
					handleNewCampaign();
				} else {
					const campaignNode = gridApi?.getRowNode(campaignId);

					if (campaignNode) {
						gridApi?.ensureNodeVisible(campaignNode);

						campaignNode.setSelected(true);
						setSelectedRowData(campaignNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [rowsCampaign, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "campaignContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2
				container
				justifyContent="flex-start"
				direction="column"
				spacing={1}
				p={2}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 container size={{ md: 12 }} spacing={2} alignItems="stretch">
					<Grid2
						size={{ xs: 12, md: 8 }}
						container
						direction="column"
						spacing={1}
					>
						<Grid2>
							<Typography variant="h5" fontWeight={600}>
								Mill Schedule
							</Typography>
						</Grid2>

						<Grid2 container spacing={1} alignItems="flex-end">
							<Grid2 size={{ xs: 12, md: 4 }}>
								<StartFilter
									value={filterStartDate}
									onChange={(date) => date && setFilterStartDate(date)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 4 }}>
								<EndFilter
									value={filterEndDate}
									onChange={(date) => date && setFilterEndDate(date)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: "grow" }}>
								<TextFieldSearchFilter
									value={filterValue}
									onChange={(val) => {
										setFilterValue(val);
										gridApi?.setGridOption("quickFilterText", val);
									}}
								/>
							</Grid2>
						</Grid2>
					</Grid2>

					<Grid2
						size={{ xs: 12, md: 4 }}
						display="flex"
						alignItems="flex-end"
						flexWrap="wrap"
						gap={1}
					>
						<HeatChips setError={setError} />
					</Grid2>
				</Grid2>

				<Grid2 sx={{ width: "100%" }}>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							<Paper
								elevation={2}
								sx={{
									height: "100%",
									width: "100%",
									"& .ag-cell": {
										fontSize: 11,
									},
								}}
							>
								<AgGridReact<Campaign>
									loading={isLoadingCampaign}
									rowData={rows || []}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									rowHeight={30}
									headerHeight={34}
									animateRows
									getContextMenuItems={getContextMenuItems}
									getRowStyle={getRowStyle}
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) => String(params.data.CampaignId)}
									loadingOverlayComponent={CenteredLazyLoading}
									onRowSelected={onRowSelected}
									gridOptions={{
										rowSelection: {
											mode: "singleRow",
											checkboxes: false,
											enableClickSelection: true,
										},
										statusBar: {
											statusPanels: [
												{
													statusPanel: "agTotalRowCountComponent",
													align: "right",
												},
											],
										},
									}}
								/>
							</Paper>
						</Panel>
						<CustomSeparator onDoubleClick={handleSeparatorDoubleClick} />
						<Panel id="details" minSize={20}>
							<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
								<CampaignDetails
									campaign={selectedRowData}
									onHide={onModalHide}
									modal={showModal}
								/>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<DeleteEntity
				show={showModal === "delete"}
				entityParams={{
					Id: selectedRowData?.CampaignId!,
					Name: `${selectedRowData?.CampaignNumber}`,
					Entity: "campaign",
				}}
				onHide={(update: boolean) => {
					onModalHide({ shouldUpdate: update, close: true });
					setShowModal(update ? "" : "update");
				}}
				deleteFunction={(campaignId: string) =>
					deleteCampaign(Number(campaignId))
				}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
